// cinematic/lens.ts (CX4) - the ONE-renderer seam. The cinematic felt (CX2) + the narrator (CX3) are bound
// into all three lenses of the North Star §5 spine - Observe (watch a pro play + narrate a full hand),
// Diagnose (you make the calls, coached in the moment), Solo (you play for real, narration held to the
// debrief) - as ONE renderer with a per-LENS config, never a fork. This module is that config: the frame
// ordinal a hero decision maps to (so the do-modes can HALT the cinematic playback at each of hero's turns),
// plus the lens vocabulary the narrator and the felt read.
//
// Cash vs tournament is table config, not a new renderer (Phase 7 rides this): nothing here branches on
// game type - the halt mapping is `history.length`, which is identical for a tournament hand. The seam is
// proven game-type-agnostic in lens.test.ts so Phase 7 slots a tournament hand through the SAME machine.
import type { HandStream } from '../engine/frameStream';
import type { LiveHandModel, LiveSnapshot } from '../engine/liveRunner';

/** The three lenses of the spine. The lens is a CONFIG the one renderer + one narrator consume - never a
 *  separate code path. `observe` = the pro plays + narrates; `diagnose` = you play, coached in the moment
 *  (villain reads live, your grade is the coaching); `solo` = you play, narration held to the debrief. */
export type Lens = 'observe' | 'diagnose' | 'solo';

/**
 * The frame ordinal at which hero is on the clock for a given decision step - the point the do-mode felt
 * HALTS its cinematic playback and asks hero to act.
 *
 * WHY `state.history.length`: the frame stream (CX1) replays a hand's ordered actions through `advance()`
 * from the true initial state, emitting one frame per action - frame `i` is the state AFTER `i` actions.
 * A decision step's `state` is the HandState at hero's turn BEFORE hero acts, so its history holds exactly
 * the `H` actions that preceded him; that state IS frame `H` (where `toAct === heroSeat`). Mid-hand there is
 * one frame per action (a street-closing action is a single `sweep`-labelled frame), so the alignment is
 * exact; only the terminal action splits into two frames (sweep + award), and hero never decides there.
 * Pinned in lens.test.ts (`frames[ord].toAct === heroSeat` for every step, on real AND provisional hands).
 */
export const heroDecisionFrame = (step: LiveSnapshot): number => step.state.history.length;

/** The halt ordinal for every hero decision in the model, in step order (preflop nodes then postflop). */
export const heroDecisionFrames = (model: LiveHandModel): number[] => model.steps.map(heroDecisionFrame);

/**
 * The frame ordinal at which hero's OWN action for step `k` becomes visible on the felt - one past his
 * decision frame. The do-modes advance here after a commit to SHOW the action hero just took (and, in
 * Diagnose, to sit on it while the grade is read) before playing on to the next decision.
 *
 * Clamped to the last frame so the final decision - whose action closes the hand and whose "+1" would be the
 * award frame - resolves to a real index the sequence can rest on.
 */
export const heroActionFrame = (step: LiveSnapshot, stream: HandStream): number =>
  Math.min(heroDecisionFrame(step) + 1, stream.frames.length - 1);
